/**
 * @fileoverview The host API for a room's companion definition (#4632 phase 3c):
 * `GET`/`PUT /api/rooms/[roomId]/companion`. A room binds a character (the body), one of the
 * approved templates or the room's kept Custom prompt, the host's words for the blanks, a name, a
 * voice and the adult-language toggle. One definition is saved per character; one is active.
 * See `_docs/Architecture/Companions/metahuman/Character-Lifecycle-Phase3-Spec.md`.
 */
import { BASE_COMPANION_TEMPLATE_ID, type CompanionTemplateBlanks } from './CompanionTemplates';

/**
 * What a room with no saved definition serves (decision D6, amended with D11 on 2026-10-10): the
 * Helpful assistant, named Telly, on Drake, with the default companion's voice (Craig).
 */
export const DEFAULT_ROOM_COMPANION = {
  characterId: 'drake',
  templateId: BASE_COMPANION_TEMPLATE_ID,
  name: 'Telly',
} as const;

/** One saved definition: what the room's companion is when it wears this character. */
export interface RoomCompanionDefinitionView {
  characterId: string;
  /** One of the approved templates, or null for the room's kept Custom prompt. */
  templateId: string | null;
  /** The host's words for the template's blanks (null for Custom). */
  blanks: CompanionTemplateBlanks | null;
  /** Custom only: what the prompt was kept from, for the picker ("Custom (kept from Telly)"). */
  customLabel: string | null;
  /** Custom only: the kept prompt, shown read-only. */
  customPrompt: string | null;
  name: string;
  /** Null: the companion's default voice. */
  voiceId: string | null;
  voiceProvider: string | null;
  adultModeEnabled: boolean;
  /** Whether this definition may turn adult language on (every template can; Custom keeps its companion's answer). */
  adultCapable: boolean;
  /**
   * The definition still carries its companion's earlier settings (brain, default mode, skills, adult
   * capability) from before templates. Choosing a template in the editor replaces them with the
   * template's.
   */
  keepsEarlierSettings: boolean;
}

/** A character a host can put on the room's companion. */
export interface RoomCompanionCharacterOption {
  id: string;
  displayName: string;
  thumbnailUrl: string | null;
  imagePreviewUrl: string | null;
  videoPreviewUrl: string | null;
  /** The voice the companion gets when the room switches to this character without a saved definition. */
  defaultVoiceId: string | null;
  defaultVoiceProvider: string | null;
}

export interface RoomCompanionGetResponse {
  /**
   * False when the room has no saved definition and is served the default one (DEFAULT_ROOM_COMPANION);
   * its first save writes its own.
   */
  bound: boolean;
  activeCharacterId: string;
  /** Every saved definition, the active one included. */
  definitions: RoomCompanionDefinitionView[];
  characters: RoomCompanionCharacterOption[];
  /** Host companion settings are switched off platform-wide; saving is refused. */
  platformDisabled: boolean;
  /** Adult language is switched off platform-wide. */
  adultModeDisabled: boolean;
  /** The room's adult content acknowledgement (Security tab). */
  acknowledged: boolean;
}

/**
 * Writes the definition for `characterId` and makes it the room's active one. Every other field is
 * optional: a field left out keeps the value of that character's saved definition, or, when the
 * character has none yet, of the active definition (so `{ characterId }` alone changes the body and
 * keeps everything else, except that a character the room has not used before brings its own default
 * voice). `templateId: null` is refused: every definition is a template.
 */
export interface RoomCompanionPutBody {
  characterId: string;
  /** Sent only when the host chose a template (or Custom): that choice also replaces any earlier settings. */
  templateId?: string | null;
  blanks?: Partial<CompanionTemplateBlanks>;
  name?: string;
  voiceId?: string | null;
  voiceProvider?: string | null;
  adultModeEnabled?: boolean;
}

export type RoomCompanionPutResponse = RoomCompanionGetResponse;

export const RoomCompanionErrorCode = {
  NOT_AUTHORIZED: 'NOT_AUTHORIZED',
  INVALID_ROOM: 'INVALID_ROOM',
  WORKFLOW_NOT_FOUND: 'WORKFLOW_NOT_FOUND',
  /** The room has no definition to start an edit from. */
  NOT_BINDABLE: 'NOT_BINDABLE',
  INVALID_CHARACTER: 'INVALID_CHARACTER',
  INVALID_TEMPLATE: 'INVALID_TEMPLATE',
  INVALID_TEMPLATE_BLANK: 'INVALID_TEMPLATE_BLANK',
  /** Custom was chosen but the room has no kept Custom prompt. */
  NO_CUSTOM_PROMPT: 'NO_CUSTOM_PROMPT',
  INVALID_CUSTOM_NAME: 'INVALID_CUSTOM_NAME',
  INVALID_VOICE: 'INVALID_VOICE',
  HOST_OVERRIDE_DISABLED: 'HOST_OVERRIDE_DISABLED',
  NOT_ADULT_CAPABLE: 'NOT_ADULT_CAPABLE',
  ADULT_ACKNOWLEDGEMENT_REQUIRED: 'ADULT_ACKNOWLEDGEMENT_REQUIRED',
  ADULT_MODE_DISABLED: 'ADULT_MODE_DISABLED',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  RATE_LIMITED: 'RATE_LIMITED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
} as const;
export type RoomCompanionErrorCode = (typeof RoomCompanionErrorCode)[keyof typeof RoomCompanionErrorCode];

export interface RoomCompanionErrorResponse {
  error: string;
  code: RoomCompanionErrorCode;
  retryAfterSec?: number;
  blanks?: Record<string, string>;
}
